/**
 * @description 腾讯视频pc端的代码实现
 * @author aprilcai
 */

import { resultCode, resultMessage, wrapResult } from '../module/entity';
import invokeAsync from '../module/pc-bridge';
import { getCookie } from '../module/util';
import { isTenvideoMac, isTenvideoWindows } from '../module/ua';
import {
  $getPayUrl,
  $getNetworkState,
  $getVolume,
  $setVolume,
  $getAvatar,
  $commonGetUserInfo,
  $openPayVipView,
  $share,
  $toast,
} from './pc-web-common-method';

// 当前webview是否支持
export function support() {
  return isTenvideoWindows;
}

// 登录
export async function login(): Promise<IResult> {
  const res = await invokeAsync('login', {});
  const { errCode, errMsg } = res;
  return wrapResult(errCode, errMsg);
}

// 退出登录
export async function logout(): Promise<IResult> {
  await invokeAsync('logout', {});
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 是否登录
export async function isLogin(): Promise<IResult> {
  const res = await invokeAsync('getLoginStatus', null);
  return wrapResult(resultCode.ok, resultMessage.ok, {
    isLogin: res.result === 1,
  });
}

// 获取用户信息
export async function getUserInfo(param: { data: any; isTest: boolean }): Promise<IResult> {
  const isLoginRes = await isLogin();
  const { result } = isLoginRes;
  const isLoginResult = result;
  if (isLoginResult) {
    const type = getCookie('main_login');
    const avatar = await $getAvatar(type);
    const userInfoRes = await $commonGetUserInfo(param);
    let userInfo = userInfoRes.result;
    if (userInfoRes.code === resultCode.ok) {
      userInfo.baseInfo.avatar = avatar;
      userInfo.type = type;
    } else {
      userInfo = {
        appid: '',
        openid: '',
        token: '',
        baseInfo: {
          nickname: '',
          vip: false,
          avatar: '',
        },
        type,
      };
    }
    return Promise.resolve(wrapResult(resultCode.ok, resultMessage.ok, userInfo));
  }
  return Promise.resolve(wrapResult(resultCode.error, `${resultMessage.error}(no login)`));
}

// 获取用户设备id
export async function getDeviceId(): Promise<IResult> {
  return wrapResult(resultCode.ok, resultMessage.ok, {
    result: {
      id: getCookie('video_guid'),
    },
  });
}

// 获取音量
export async function getVolume(): Promise<IResult> {
  return $getVolume();
}

// 设置音量
export async function setVolume(param: { volume: number }): Promise<IResult> {
  return $setVolume(param);
}

// 获取网络状态
export async function getNetworkState(): Promise<IResult> {
  return $getNetworkState();
}

// 打开webview
export async function openWebview(param: {
  url: string;
  close?: number;
  style?: number;
}): Promise<IResult> {
  const method = isTenvideoMac ? 'windowOpen' : 'openWindow';
  await invokeAsync(method, {
    url: param.url,
    width: window.innerWidth,
    height: window.innerHeight,
    forceOpen: true,
  });
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 打开支付页
export async function openPayPage(param: {
  appid?: string;
  actid?: string;
  orderid?: string;
  needpay?: string;
  close?: number;
  ru?: string;
  title?: string;
  sandbox?: number;
  autopay?: number;
}): Promise<IResult> {
  const { result } = await $getPayUrl(param);
  const method = isTenvideoMac ? 'windowOpen' : 'openWindow';
  await invokeAsync(method, {
    url: result.payUrl,
    width: window.innerWidth,
    height: window.innerHeight,
    forceOpen: true,
  });
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 修改标题
export async function refreshTitle(param: { title: string }): Promise<IResult> {
  await invokeAsync('changeTitle', param);
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 分享
export async function share(param: {
  title: string;
  subTitle: string;
  url: string;
  imageUrl: string;
  sharePageTitle: string;
  sharePageLaunchUrl: string;
}): Promise<IResult> {
  return $share(param);
}

// 写日志
export async function writeLog(param: {
  appid: string;
  content: string;
  isReportServer: boolean;
  isConsoleLog: boolean;
}): Promise<IResult> {
  const { appid, content, isConsoleLog } = param;
  if (isConsoleLog) {
    const logContent = `bridge-helper: ${new Date().toLocaleString()}, appid=${appid}, content=${content}`;
    window.console.log(logContent);
  }
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 打开VIP开通页/浮层
export async function openPayVipView(param: {
  actid?: string;
  nt?: string;
  n?: string;
  sp?: string;
  ptag?: string;
  aid?: string;
  dsp?: string;
  tab?: string;
  dnp?: string;
  unwx?: string;
}) {
  return $openPayVipView(param);
}

// toast
export async function toast(content: string) {
  return $toast(content);
}
